import React, { useRef } from 'react';
import {
  AppBar,
  Typography,
  IconButton,
  Box,
  Menu,
  MenuItem,
  useMediaQuery,
  Theme,
  Divider,
  Avatar,
  Skeleton,
  useTheme,
} from '@material-ui/core';
import { Location } from 'history';
import { Link, NavLink, match } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu as MenuIcon } from '@material-ui/icons';

import { getInitials, useLogout } from '../../utils';
import { useConfig } from '../../providers/Config';
import { useProfile } from '../../providers/Profile';
import Logo from '../Logo';
import { navbarLinks } from './utils';
import { Permissions } from '../Permissions';
import Icon from '../Icon';
import { IconSize } from '../../types';
import { FeatureFlag } from '../FeatureFlag';

interface HeaderProps {
  height?: number;
  mode: 'simple' | 'complete';
  simpleHeaderTitle?: string;
}

/**
 * Header component containing language picker and various links
 *
 * @param height - height pixels number
 * @param mode - 'simple' for a header with a logo only, or 'complete' for a header with all the functionality
 * @param simpleHeaderTitle - optional page title which is rendered if mode is set to "simple"
 *
 * @returns a header component with the specified height
 */
const Header: React.FC<HeaderProps> = ({ height = 80, mode = 'complete', simpleHeaderTitle }: HeaderProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { handleSignOut } = useLogout();
  const [profileMenu, setProfileMenu] = React.useState(null);
  const [navbarMenu, setNavbarMenu] = React.useState(null);
  const {
    tenantName,
    tenantDisplay: {
      logos: { logo },
    },
  } = useConfig();
  const { data, isLoading } = useProfile();
  const { familyName = '', givenName = '', profilePhoto } = data || {};
  const ref = useRef(null);

  const smUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));

  const LogoComponent = (
    <Link to={mode === 'complete' ? `/app` : '/'}>
      <Logo padding={0} logoURL={logo} tenantName={tenantName} />
    </Link>
  );

  const SimpleHeaderTitle = simpleHeaderTitle ? (
    <Typography variant="h6" component="h1" sx={{ textAlign: 'center' }}>
      {simpleHeaderTitle}
    </Typography>
  ) : null;

  return (
    <AppBar
      position="static"
      color={mode === 'simple' ? 'secondary' : 'default'}
      elevation={0}
      data-testid={`${mode}-navbar`}
      sx={{
        height: `${height}px`,
        px: 6,
        display: 'flex',
        justifyContent: `${mode === 'complete' && smUp ? 'center' : 'space-between'}`,
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      {mode === 'simple' && (
        <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
          <Box ref={ref} mr={smUp ? undefined : 4}>
            {LogoComponent}
          </Box>
          {SimpleHeaderTitle}
          {/* As the logo width varies, use a ref to get the width and then add a box of the same size on the right
          to keep the title centred on desktop */}
          {smUp && <Box width={ref?.current?.offsetWidth || 0} height={0} />}
        </Box>
      )}

      {mode === 'complete' && smUp && (
        <Box sx={{ width: { sm: '372px', md: '448px' }, display: 'flex', justifyItems: 'flex-start' }}>
          {LogoComponent}
        </Box>
      )}

      {mode === 'complete' && !smUp && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <IconButton
            color="inherit"
            aria-label="mobile-menu"
            onClick={(event: React.SyntheticEvent) => {
              setNavbarMenu(event.currentTarget);
            }}
            data-testid="mobile-menu"
            sx={{
              pl: 0,
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <MenuIcon />
          </IconButton>
          <Menu
            data-testid="mobile-menu-options"
            anchorEl={navbarMenu}
            keepMounted
            open={Boolean(navbarMenu)}
            onClose={() => {
              setNavbarMenu(null);
            }}
          >
            {navbarLinks.map(({ label, href, iconSubFolder, translation, requiredRoles, requiredFeatureFlags }) => (
              <Permissions requiredRoles={requiredRoles} key={label}>
                <FeatureFlag requiredFeatureFlags={requiredFeatureFlags}>
                  <MenuItem component={Link} to={href}>
                    <Icon size={IconSize.MD} subfolder={iconSubFolder} />
                    <Typography ml={2}>{t(`ns.${translation}`)}</Typography>
                  </MenuItem>
                </FeatureFlag>
              </Permissions>
            ))}
          </Menu>
          <Box>{LogoComponent}</Box>
        </Box>
      )}

      {/* Icons available on all devices */}
      {mode === 'complete' && smUp && (
        <Box display="flex" flexWrap="nowrap" flex={{ sm: 1 }} height="100%">
          {navbarLinks.map(
            ({
              label,
              href,
              matchExact,
              matchPath,
              iconSubFolder,
              translation,
              requiredRoles,
              requiredFeatureFlags,
            }) => (
              <Permissions requiredRoles={requiredRoles} key={label}>
                <FeatureFlag requiredFeatureFlags={requiredFeatureFlags}>
                  <IconButton
                    disableRipple
                    sx={{
                      borderRadius: '0',
                      '&:focus': {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: '-2px',
                      },
                      '&:hover': {
                        backgroundColor: theme.palette.background.default,
                      },
                      '&.active': {
                        borderBottom: `3px solid ${theme.palette.primary.main}`,
                      },
                      height: '100%',
                    }}
                    color="inherit"
                    aria-label={label}
                    component={NavLink}
                    to={href}
                    isActive={(match: match, location: Location) => {
                      if (matchExact) return match?.isExact;
                      if (matchPath && location.pathname?.match(matchPath)) return true;
                      if (match) return true;
                    }}
                  >
                    <Icon size={IconSize.MD} subfolder={iconSubFolder} />
                    <Typography variant="body1" ml={2}>
                      {t(`ns.${translation}`)}
                    </Typography>
                  </IconButton>
                </FeatureFlag>
              </Permissions>
            ),
          )}
        </Box>
      )}

      {/* >= sm right side menu icons */}
      {mode === 'complete' ? (
        <Box>
          {/*<IconButton color="inherit" aria-label="notifications">
              <NotificationsIcon />
            </IconButton> */}
          {isLoading ? (
            <Skeleton animation="wave" variant="circular" />
          ) : (
            <Avatar
              data-testid="menu-icon"
              onClick={(event: React.SyntheticEvent) => {
                setProfileMenu(event.currentTarget);
              }}
              src={profilePhoto?.filePath}
              sx={{ bgcolor: 'grey.900', color: 'background.paper', width: '32px', height: '32px' }}
            >
              <Typography variant="h6">{getInitials(givenName, familyName)}</Typography>
            </Avatar>
          )}
          <Menu
            data-testid="menu"
            anchorEl={profileMenu}
            keepMounted
            open={Boolean(profileMenu)}
            onClose={() => {
              setProfileMenu(null);
            }}
          >
            <MenuItem
              component={Link}
              to={{ pathname: '/app/account', state: { from: location.pathname } }}
              disabled={location.pathname.includes('account')}
              data-testid="my-account-btn"
            >
              {t('ns.global:header:nav:my_account')}
            </MenuItem>
            <Divider />
            <MenuItem
              aria-label={t('ns.common:sign_out:button')}
              data-testid="sign-out"
              onClick={() => handleSignOut()}
            >
              {t('ns.global:header:nav:logout')}
            </MenuItem>
          </Menu>
        </Box>
      ) : null}
    </AppBar>
  );
};

export default Header;
